/**
 * SelectedStandContext — shares the current typed Map selection between the
 * registered renderer and the route-owned descriptor builder.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface SelectedStandBase {
  standId: string;
  propertyId: string;
  inventoryRevision: string;
  /** Raw GeoJSON feature properties from the clicked stand layer. */
  properties: Record<string, unknown>;
}

export type SelectedStandData = SelectedStandBase &
  (
    | { kind: "stand" }
    | {
        kind: "risk";
        advisoryEvaluatedAt: string;
        advisoryRevision: string;
      }
  );

interface SelectedStandContextType {
  selectedStand: SelectedStandData | null;
  selectionIntentRevision: number;
  setSelectedStand: (stand: SelectedStandData | null) => void;
  hydrateSelectedStand: (stand: SelectedStandData | null) => void;
}

const SelectedStandContext = createContext<
  SelectedStandContextType | undefined
>(undefined);

export const SelectedStandProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [selectedStand, setSelectedStandState] =
    useState<SelectedStandData | null>(null);
  const [selectionIntentRevision, setSelectionIntentRevision] = useState(0);
  const setSelectedStand = useCallback((stand: SelectedStandData | null) => {
    setSelectedStandState(stand);
    setSelectionIntentRevision((revision) => revision + 1);
  }, []);
  const hydrateSelectedStand = useCallback(
    (stand: SelectedStandData | null) => setSelectedStandState(stand),
    [],
  );
  const value = useMemo(
    () => ({
      hydrateSelectedStand,
      selectedStand,
      selectionIntentRevision,
      setSelectedStand,
    }),
    [
      hydrateSelectedStand,
      selectedStand,
      selectionIntentRevision,
      setSelectedStand,
    ],
  );

  return (
    <SelectedStandContext.Provider value={value}>
      {children}
    </SelectedStandContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- standard context hook; same-file export is the project pattern (AuthContext, MapContext)
export const useSelectedStand = (): SelectedStandContextType => {
  const ctx = useContext(SelectedStandContext);
  if (!ctx)
    throw new Error(
      "useSelectedStand must be used within SelectedStandProvider",
    );
  return ctx;
};
